import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useCreateAgencyMutation, useUpdateAgencyMutation, useGetAgencyByIdQuery } from '../../slices/agencyApiSlice';

import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'react-bootstrap';


const tiers = ['Tier1', 'Tier2', 'Tier3', 'Tier4'];

const AgencyFormScreen = () => {
  const { id } = useParams(); // for edit
  const isEdit = Boolean(id);
const navigate = useNavigate();

  const [form, setForm] = useState({
    agencyName: '',
    agencyAddress: '',
    agencyPhone: '',
    agencyEmail: '',
    agencyTier: '',
    daysToShiftStart:100,
    // Password is never pre-filled from the server (it's never returned),
    // so this always starts blank. On create it's the agency's initial
    // password; on edit, leaving it blank keeps whatever password the
    // agency already has.
    password: '',
    isApproved: false,
    isActive: true,
  });

  const { data: agency } = useGetAgencyByIdQuery(id, { skip: !isEdit });
  const [createAgency, { isLoading: isCreating }] = useCreateAgencyMutation();
  const [updateAgency, { isLoading: isUpdating }] = useUpdateAgencyMutation();

  useEffect(() => {
  if (agency) {
   //console.log('Loaded agency data:', agency); // Debugging
    setForm((prev) => ({
      ...prev,
      agencyName: agency.agencyName || '',
      agencyAddress: agency.agencyAddress || '',
      agencyPhone: agency.agencyPhone || '',
      agencyEmail: agency.agencyEmail || '',
      agencyTier: agency.agencyTier || '',
      daysToShiftStart: agency.daysToShiftStart ?? 100, // default to 100 only if undefined
      isApproved: agency.isApproved ?? false,
      isActive: agency.isActive ?? true,
      // password intentionally not touched here — stays blank
    }));
  }
}, [agency]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Never send an empty password on edit — the backend already ignores
    // a blank one, but skipping it here keeps the intent explicit at the
    // call site too.
    const { password, ...rest } = form;
    const payload = password.trim() ? { ...rest, password } : rest;

    try {
      if (isEdit) {
        await updateAgency({ id, ...payload }).unwrap();
        toast.success('Agency updated');
      } else {
        await createAgency(payload).unwrap();
        toast.success('Agency created');
      }
      navigate('/admin/agencies');
    } catch (err) {
      // Previously this form didn't check for an error at all — it always
      // showed "Agency saved!" and navigated away even when the request
      // failed, which made backend failures (bad phone format, duplicate
      // email, missing password, etc.) invisible.
      toast.error(err?.data?.message || err?.error || 'Failed to save agency');
    }
  };

  const isSaving = isCreating || isUpdating;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, maxWidth: 500 }}>
       <Card className="mb-4 custom-card-bg2">
  <Card.Header className="fw-bold">Agency Information</Card.Header>
  <Card.Body>

      <Typography variant="h5" mb={2}>
        {isEdit ? 'Edit Agency' : 'Create Agency'}
      </Typography>

      <TextField
        label="Name"
        name="agencyName"
        fullWidth
        margin="normal"
        value={form.agencyName}
        onChange={handleChange}
        required
      />
      <TextField
        label="Address"
        name="agencyAddress"
        fullWidth
        margin="normal"
        value={form.agencyAddress}
        onChange={handleChange}
        required
      />
      <TextField
        label="Phone"
        name="agencyPhone"
        fullWidth
        margin="normal"
        value={form.agencyPhone}
        onChange={handleChange}
        required
      />
      <TextField
        label="Email"
        name="agencyEmail"
        type="email"
        fullWidth
        margin="normal"
        value={form.agencyEmail}
        onChange={handleChange}
        required
      />

      <TextField
        label={isEdit ? 'Reset Password' : 'Password'}
        name="password"
        type="password"
        fullWidth
        margin="normal"
        value={form.password}
        onChange={handleChange}
        required={!isEdit}
        autoComplete="new-password"
        helperText={
          isEdit
            ? 'Leave blank to keep the current password'
            : 'The agency will use this password to log in to the portal'
        }
      />

      <TextField
        label="Tier"
        name="agencyTier"
        select
        fullWidth
        margin="normal"
        value={form.agencyTier}
        onChange={handleChange}
        required
      >
        {tiers.map((tier) => (
          <MenuItem key={tier} value={tier}>
            {tier}
          </MenuItem>
        ))}
      </TextField>

    <TextField
  label="Visible to agency in x number of days to start of shift"
  name="daysToShiftStart"
  type="number"
  fullWidth
  margin="normal"
  value={form.daysToShiftStart}
  onChange={(e) => handleChange({ target: { name: e.target.name, value: Number(e.target.value) } })}
  required
  inputProps={{ min: 1, max: 100 }}
/>

      <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Switch
              name="isApproved"
              checked={form.isApproved}
              onChange={handleSwitchChange}
            />
          }
          label="Approved (can log in)"
        />
        <FormControlLabel
          control={
            <Switch
              name="isActive"
              checked={form.isActive}
              onChange={handleSwitchChange}
            />
          }
          label="Active"
        />
      </Stack>

      <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={isSaving}>
        {isEdit ? 'Update' : 'Create'}
      </Button>

        </Card.Body>
      </Card>

    </Box>
  );
};

export default AgencyFormScreen;
